import {
  API_BASE_URL,
  API_TIMEOUT_MS,
  DEFAULT_CHECK_INTERVAL_MINUTES,
  DEFAULT_CHECK_ROUNDS,
  DEFAULT_ROUND_INTERVAL_SECONDS,
  MAX_CHECK_ROUNDS,
  MAX_ROUND_INTERVAL_SECONDS,
  MAX_TOTAL_ROUND_GAP_SECONDS,
  MIN_CHECK_INTERVAL_MINUTES,
  MIN_CHECK_ROUNDS,
} from './config';
import { timeoutSignal } from './timeout';
import type { Link, RunConfig, SubmitRequest, SubmitResponse } from './types';

/**
 * Our own backend, `device-api/*`.
 *
 * **Mode `cors`, and a real header.** This is the half of the app that reads
 * JSON, which `no-cors` cannot do (SPEC §5) — mixing the two modes up breaks a
 * different thing on each side, so they live in separate files: this one talks
 * to us, `probe.ts` talks to strangers.
 *
 * Requires the backend to allow the dashboard's origin, **including the
 * `OPTIONS` preflight** — `X-Device-Key` is not a CORS-safelisted header, so the
 * browser asks permission before every one of these calls, GETs included. That
 * dependency is new relative to Android, which uses OkHttp and is not subject to
 * CORS at all. See SPEC §16, and `functions/api/[[path]].ts` for the way around
 * it that does not need the backend to change.
 */

/** A non-2xx answer from our backend. `status` is what the caller branches on. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    message?: string,
  ) {
    super(message ?? `HTTP ${status} on ${path}`);
    this.name = 'ApiError';
  }
}

/** `401` — the key is dead. Never returned for a transient fault; see API.md. */
export const UNAUTHORIZED = 401;

export interface ApiClient {
  me(): Promise<{ id: number; name: string; region?: string | null }>;
  config(): Promise<RunConfig>;
  links(): Promise<Link[]>;
  submit(body: SubmitRequest): Promise<SubmitResponse>;
}

export function createApiClient(
  key: string,
  baseUrl: string = API_BASE_URL,
  fetchImpl: typeof fetch = globalThis.fetch,
): ApiClient {
  async function call<T>(path: string, init?: RequestInit): Promise<T> {
    const { signal, cancel } = timeoutSignal(API_TIMEOUT_MS);
    try {
      const response = await fetchImpl(`${baseUrl}/${path}`, {
        ...init,
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal,
        headers: {
          Accept: 'application/json',
          // The device key goes **only** here. Never on a probe — same rule as
          // the Android app's `AuthInterceptor`, which is wired to the API
          // client and deliberately not to the check client.
          'X-Device-Key': key,
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...init?.headers,
        },
      });
      if (!response.ok) {
        throw new ApiError(response.status, path, await errorMessage(response, path));
      }
      return (await response.json()) as T;
    } finally {
      cancel();
    }
  }

  return {
    me: () => call('device-api/me'),

    /**
     * How this device should run (SPEC §7.2, §9).
     *
     * **A dead config endpoint must never kill a cycle.** Anything short of a
     * `401` — network error, 404, 500, a payload whose shape moved — falls back
     * to the defaults and the cycle runs. Losing a cycle's config is a shrug;
     * losing the cycle is not.
     *
     * `401` is the one exception, and it is a constraint on the backend as much
     * as behaviour here: it means the key is dead for good, so the app stops
     * rather than looping forever against a door that will not open. A `401`
     * returned for a transient fault (a half-finished deploy, a misconfigured
     * gateway) strands a field device until somebody physically re-provisions
     * it. Transient faults must be `5xx`. See API.md.
     */
    async config(): Promise<RunConfig> {
      try {
        const raw = await call<Record<string, unknown>>('device-api/config');
        return clampConfig(raw, true);
      } catch (error) {
        if (error instanceof ApiError && error.status === UNAUTHORIZED) throw error;
        return FALLBACK_CONFIG;
      }
    },

    async links(): Promise<Link[]> {
      const raw = await call<unknown>('device-api/links');
      if (!Array.isArray(raw)) return [];
      return raw.flatMap(toLink);
    },

    submit: (body) =>
      call('device-api/results', { method: 'POST', body: JSON.stringify(body) }),
  };
}

/** What a cycle runs on when the server cannot be reached. See SPEC §7.2. */
export const FALLBACK_CONFIG: RunConfig = {
  autoCheckEnabled: true,
  checkIntervalMinutes: DEFAULT_CHECK_INTERVAL_MINUTES,
  checkRounds: DEFAULT_CHECK_ROUNDS,
  roundIntervalSeconds: DEFAULT_ROUND_INTERVAL_SECONDS,
  fromServer: false,
};

/**
 * Reads the config payload defensively and clamps it on the way in, so nothing
 * downstream has to remember to.
 *
 * **Every field may be missing, null, or of the wrong type.** The endpoint was
 * built for this feature and its shape may still move; a field that arrives
 * wrong must fall back rather than throw.
 *
 * The clamps are not defensive padding. `checkRounds` and `roundIntervalSeconds`
 * decide how long a cycle takes, and one mistyped number on the server would
 * otherwise put every field device into a run that never ends — on this app, a
 * run nobody can watch to completion because the screen has to stay awake for it.
 */
export function clampConfig(raw: Record<string, unknown>, fromServer: boolean): RunConfig {
  const rounds = clamp(
    int(raw.checkRounds, DEFAULT_CHECK_ROUNDS),
    MIN_CHECK_ROUNDS,
    MAX_CHECK_ROUNDS,
  );
  const requestedGap = clamp(
    int(raw.roundIntervalSeconds, DEFAULT_ROUND_INTERVAL_SECONDS),
    0,
    MAX_ROUND_INTERVAL_SECONDS,
  );
  return {
    autoCheckEnabled: typeof raw.autoCheckEnabled === 'boolean' ? raw.autoCheckEnabled : true,
    checkIntervalMinutes: Math.max(
      MIN_CHECK_INTERVAL_MINUTES,
      int(raw.checkIntervalMinutes, DEFAULT_CHECK_INTERVAL_MINUTES),
    ),
    checkRounds: rounds,
    // The product of the two is what has to be bounded, not each on its own:
    // 5 rounds and a 300s gap are individually reasonable and together mean 20
    // minutes of pure waiting. An interval that would overshoot is cut to fit.
    roundIntervalSeconds: cappedGap(requestedGap, rounds),
    fromServer,
  };
}

/** The per-gap value that keeps `gap × (rounds − 1)` inside the total budget. */
export function cappedGap(seconds: number, rounds: number): number {
  const gaps = Math.max(0, rounds - 1);
  if (gaps === 0 || seconds * gaps <= MAX_TOTAL_ROUND_GAP_SECONDS) return seconds;
  return Math.floor(MAX_TOTAL_ROUND_GAP_SECONDS / gaps);
}

/** `{ id, brand, domain, expiryAt }` → `Link`. Rows without a usable domain are dropped. */
function toLink(raw: unknown): Link[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const row = raw as Record<string, unknown>;
  const domain = typeof row.domain === 'string' ? row.domain.trim() : '';
  if (domain === '') return [];
  const id = row.id;
  if (typeof id !== 'number' && typeof id !== 'string') return [];
  return [
    {
      // Kept as a string, exactly as Android does — results are reported by
      // `brandLinkId`, and the id travels further than the URL does.
      id: String(id),
      url: domain,
      brand: typeof row.brand === 'string' ? row.brand : null,
    },
  ];
}

function int(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? Math.round(n) : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Best-effort body text for the error message; never masks the status itself. */
async function errorMessage(response: Response, path: string): Promise<string> {
  try {
    const text = (await response.text()).replace(/\s+/g, ' ').trim();
    return text === '' ? `HTTP ${response.status} on ${path}` : `HTTP ${response.status} on ${path}: ${text.slice(0, 200)}`;
  } catch {
    return `HTTP ${response.status} on ${path}`;
  }
}
